import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Components
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";

// Services

import { previewDirectors, updateDirectorIndex } from "@/services/directors";
import { S3_BUCKET_LINK } from "@/components/constants";

// Types
import type { Directors } from "@/types/directors";

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/director/preview",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { toast } = useToast();
	const theid = localStorage.getItem("companyId") || "";
	const companyId = theid.replace(/^"(.+)"$/, "$1");

	console.log("theid", theid);

	// States
	const [items, setItems] = useState<Directors[]>([]);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	// Query
	const { data: dsiData, isLoading } = useQuery({
		queryKey: ["directors-preview"],
		queryFn: () =>
			previewDirectors({
				companyId: companyId,
			}),
	});

	// Mutation
	const updateIndexesMutation = useMutation({
		mutationFn: updateDirectorIndex,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["directors-preview"] });
			queryClient.invalidateQueries({ queryKey: ["directors"] });

			toast({
				title: "ສຳເລັດ",
				description: "ອັບເດດລຳດັບສຳເລັດແລ້ວ",
			});
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	const dsi = dsiData?.data;

	console.log("boards", dsi);

	// Functions
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (active.id !== over?.id) {
			setItems((items) => {
				const oldIndex = items.findIndex((item) => item.id === active.id);
				const newIndex = items.findIndex((item) => item.id === over?.id);

				const newItems = arrayMove(items, oldIndex, newIndex);

				const updateIndexes = newItems.map((item, index) => ({
					id: item.id,
					index: index + 1,
				}));

				updateIndexesMutation.mutate({ updateIndexes });

				return newItems;
			});
		}
	};

	// Effects
	useEffect(() => {
		if (dsi?.length > 0) {
			setItems(dsi);
		}
	}, [dsi]);

	if (isLoading) {
		return (
			<div className="flex flex-col p-[80px] gap-[40px]">
				<Skeleton className="w-full h-[100px] rounded-md" />

				<Skeleton className="w-full h-[500px] rounded-md" />
			</div>
		);
	}

	return (
		<div className="flex flex-col  gap-[40px]">
			<Button
				className="self-start"
				variant={"outline"}
				onClick={() => navigate({ to: ".." })}
			>
				ກັບຄືນ
			</Button>

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={items.map((item: Directors) => item.id)}
					strategy={verticalListSortingStrategy}
				>
					<div className="space-y-[40px]">
						{items.map((item: Directors, index: number) => (
							<SortableProfileCard key={item.id} item={item} no={index} />
						))}
					</div>
				</SortableContext>
			</DndContext>
		</div>
	);
}

const SortableProfileCard = ({ item, no }: { item: Directors; no: number }) => {
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id: item.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes}>
			<div className="flex gap-[24px]">
				{/* Drag handle - only this part should trigger drag */}
				<div className="flex items-center gap-2">
					<div
						{...listeners} // Move listeners here only
						className="p-2 transition-colors bg-gray-100 rounded cursor-grab active:cursor-grabbing hover:bg-gray-200"
						title="Drag to reorder"
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
							<path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM20 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM20 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM20 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
						</svg>
					</div>
					<p className="font-bold text-primary text-[80px]">{no + 1}</p>
				</div>

				{/* Content area - no drag listeners */}
				<div className="flex-1">
					<ProfileCard item={item} no={no} />
				</div>
			</div>
		</div>
	);
};

const ProfileCard = ({ item, no }: { item: Directors; no: number }) => {
	return (
		<div className="flex gap-[24px] ">
			<Accordion type={"multiple"} className="w-full">
				<div className="flex-1 flex bg-[#216C4C33] rounded-lg overflow-hidden relative">
					<div className="w-[209px] h-full relative">
						<img
							sizes="100%"
							src={S3_BUCKET_LINK + item?.profileUrl}
							alt="profile"
							className="object-cover"
						/>
					</div>

					<div className="flex-1 p-[24px]">
						<h2 className="text-[24px] font-bold">
							ທ່ານ {item?.gender === "FEMALE" ? "ນາງ" : ""} {item?.firstName}{" "}
							{item?.lastName}
						</h2>
						<p className="mt-[8px] text-[20px]">{item?.position}</p>

						<div className="space-y-[8px] mt-[24px]">
							<p className="font-bold ">ຊິວະປະຫວັດຫຍໍ້:</p>

							<AccordionItem value="education">
								<AccordionTrigger
									className="hover:no-underline"
									onClick={(e) => {
										// Prevent event bubbling to drag handlers
										e.stopPropagation();
									}}
								>
									ວຸດທິການສຶກສາ
								</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-4 text-balance">
									<p>{item?.education}</p>
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="experience">
								<AccordionTrigger
									className="hover:no-underline"
									onClick={(e) => {
										// Prevent event bubbling to drag handlers
										e.stopPropagation();
									}}
								>
									ປະສົບການ
								</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-4 text-balance">
									<p>{item?.experience}</p>
								</AccordionContent>
							</AccordionItem>
						</div>
					</div>

					{/* <img
            alt="profile"
            src={"/images/dsi-logo.png"}
            className="absolute top-2 right-2 opacity-10"
            width={350}
            height={350}
          /> */}
				</div>
			</Accordion>
		</div>
	);
};
